/**
 * The one zoom control. All four charts share a single x domain, so there is
 * exactly one of these, overlaid above the readout rail and revealed on hover.
 * The scroll wheel and double-click still work on any chart.
 *
 * The dropdown always reads the *actual* ceiling, not "custom" — after a scroll
 * or a +/- click the number it shows is the evolution the plot ends at.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { ZOOM_PRESETS, mutagenToReachEvo, parseCompact } from '../model';
import { useLab } from './LabContext';

/** One click of +/-. */
const ZOOM_STEP = 1.4;

const CUSTOM = '__custom__';

export function ZoomControls() {
  const { ruleset, maxMutagen, maxEvo, zoomBy, setMaxEvo, resetZoom } = useLab();
  const [draft, setDraft] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (draft !== null) inputRef.current?.select();
  }, [draft !== null]);

  // The same evolution is a different mutagen ceiling on each branch, and
  // bisections are not free, so resolve the presets once per branch.
  const presets = useMemo(
    () =>
      ZOOM_PRESETS.map((preset) => ({
        ...preset,
        mutagen: mutagenToReachEvo(ruleset, preset.maxEvo, 0),
      })),
    [ruleset],
  );

  const preset = presets.find((entry) => Math.abs(entry.mutagen - maxMutagen) < maxMutagen * 1e-6);
  const live = `${Math.round(maxEvo * 100)}%`;

  const commit = () => {
    const text = draft;
    setDraft(null);
    const next = text === null ? null : parseCompact(text);
    if (next !== null && next > 0) setMaxEvo(Math.min(next, 1200) / 100);
  };

  return (
    <div className="chart-zoom" role="group" aria-label="Zoom">
      <button type="button" title="Zoom out" onClick={() => zoomBy(ZOOM_STEP)}>
        −
      </button>
      <button type="button" title="Zoom in" onClick={() => zoomBy(1 / ZOOM_STEP)}>
        +
      </button>

      {draft === null ? (
        <select
          aria-label="Zoom to evolution"
          title="Show up to this evolution"
          value={preset ? preset.label : live}
          onChange={(event) => {
            const value = event.target.value;
            if (value === CUSTOM) {
              setDraft(String(Math.round(maxEvo * 100)));
              return;
            }
            const chosen = presets.find((entry) => entry.label === value);
            if (chosen) setMaxEvo(chosen.maxEvo);
          }}
        >
          {/* The current ceiling, whenever it is not one of the presets. */}
          {preset ? null : <option value={live}>{live}</option>}
          {presets.map((entry) => (
            <option key={entry.label} value={entry.label}>
              {entry.label}
            </option>
          ))}
          <option value={CUSTOM}>Custom…</option>
        </select>
      ) : (
        <span className="number-field editing">
          <input
            ref={inputRef}
            type="text"
            inputMode="decimal"
            aria-label="Zoom to evolution"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={commit}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                commit();
              } else if (event.key === 'Escape') {
                setDraft(null);
              }
            }}
          />
        </span>
      )}

      <button type="button" title="Reset zoom" onClick={resetZoom}>
        ⟲
      </button>
    </div>
  );
}
